// @flow
import { call, put, select, delay } from 'redux-saga/effects';
import { isImmutable, OrderedSet } from 'immutable';
import AxiosFactory from 'utils/axiosFactory';
import { getLogger } from 'utils/logger';
import { Config, ENVIRONMENT } from 'utils/webappConfig';
import { addPostponedAction } from 'data/postponedActions/postponedActionsActions';
import { shouldPostponeAction } from 'data/postponedActions/postponedActionsHelpers';
import { setAppProps } from 'data/app/actions';
import store from 'store';

import { mkQcsSyncResourcesPayload } from './qcsSyncResourceTypes';
import type { QcsSyncResourcesConfig } from './qcsSyncResourceTypes';

const { sprintf } = require('sprintf-js');

const qcsAxios = AxiosFactory.get('qcs');
const log = getLogger('data/qcsSyncResourceSagaHelpers');


export function* getQcsSyncResources(config: QcsSyncResourcesConfig, action: any): Generator<*, *, *> {
  try {
    let lastSyncDate;
    if (config.getLastSyncDate) {
      lastSyncDate = yield select(config.getLastSyncDate);
    }

    const queryParams = { ...lastSyncDate && { modifiedAfter: lastSyncDate }, limit: 1000 };
    let requestUrl = `${Config.environments[ENVIRONMENT].services_url}${config.resourceBaseUrl}`;
    const axiosConfig = {
      ...(config.axiosConfig || {}),
      ...(action.meta?.axiosConfig || {}),
      params: {
        ...queryParams,
        ...(config.axiosConfig?.params || {}),
        ...(action.meta?.axiosConfig?.params || {}),
      },
    };

    const actionSuccessPayload = {
      resources: [],
      lastSyncDate: null,
    };

    while (requestUrl) {
      const response = yield call(qcsAxios.get, requestUrl, axiosConfig);

      // If this is the first page of data, update lastSyncDate with the asOf
      // time from the server so we can send it back the next time we retrieve
      // we retrieve resources.
      //
      if (response.data.metaData) {
        if (response.data.metaData.currentPage === 1) {
          actionSuccessPayload.lastSyncDate = response.data.metaData.asOf;
        }
        const nextPage = response.data.metaData.nextLink;
        requestUrl = nextPage ? `${Config.environments[ENVIRONMENT].services_url}${nextPage}` : null;
      } else {
        requestUrl = undefined;
      }

      const resources = config.transformResponseToResources(response);
      // log.debug(`Transformed ${config.resourceName}s`, response, resources);

      actionSuccessPayload.resources = actionSuccessPayload.resources.concat(resources || []);
    }
    yield put(config.successAction(mkQcsSyncResourcesPayload(actionSuccessPayload), action.meta));
  } catch (error) {
    log.error(`Error retrieving resource "${config.resourceName}": ${error}`);
    // todo: this may not be an axios error, could be other exception
    yield put(config.failureAction(error, action.meta));

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////
////////////////////////////
////////////////////////////////////////////////////////////////////////////
////////
/////////////
  }
}

export function* qcsSyncCreateResource(config: any, action: any): Generator<*, *, *> {
  const { payload: resource } = action;

  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}${config.resourceBaseUrl}`;
  const requestData = config.transformResourceToRequestData(resource);

  let response;
  try {
    response = yield call(qcsAxios.post, requestUrl, requestData, (action.meta && action.meta.axiosConfig) || config.axiosConfig);
    const resultingResource = config.transformResourceWithResponse(resource, response);
    yield put(config.successAction(resultingResource, { ...action.meta, response }));

    if (action.meta && action.meta.afterSuccessAction) {
      yield put(action.meta.afterSuccessAction);
    }
  } catch (error) {
    log.error(`Error creating ${config.resourceName}`, error);
    const payload = {
      clientId: resource.clientId,
      ...error,
    };
    if (shouldPostponeAction(action, error)) {
      yield put(addPostponedAction({ action, error, onFailure: () => store.dispatch(config.failureAction(payload, action.meta)) }));
    } else {
      yield put(config.failureAction(payload, action.meta));
    }
    return error;
  }

  return response;
}

export function* qcsSyncUpdateResource(config: QcsSyncResourcesConfig, action: any): Generator<*, *, *> {
  const { payload: resource } = action;
  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}${config.resourceBaseUrl}${resource.id ? `/${resource.id}` : ''}`;
  const requestData = config.transformResourceToRequestData(resource);

  let response;
  try {
    response = yield call(qcsAxios.put, requestUrl, requestData, (action.meta && action.meta.axiosConfig) || config.axiosConfig);
    yield put(config.successAction(resource, { ...action.meta, response }));
  } catch (error) {
    log.error(`Error updating ${config.resourceName}`, error);
    const payload = {
      clientId: resource.clientId,
      ...error,
    };
    if (shouldPostponeAction(action, error)) {
      yield put(addPostponedAction({ action, error, onFailure: () => store.dispatch(config.failureAction(payload, action.meta)) }));
    } else {
      yield put(config.failureAction(payload, action.meta));
    }
  }

  return response;
}

export function* qcsSyncGetResource(config: QcsSyncResourcesConfig, action: any): Generator<*, *, *> {
  const { payload: resource, asyncDispatch } = action;
  const baseUrl = sprintf(config.resourceBaseUrl, resource && resource.id);
  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}${baseUrl}`;

  let response;
  try {
    response = yield call(qcsAxios.get, requestUrl, (action.meta && action.meta.axiosConfig) || config.axiosConfig);
    const resources = [config.transformResponseToResource(response)];
    yield put(config.successAction(mkQcsSyncResourcesPayload({ resources, asyncDispatch }), { ...action.meta, response }));
  } catch (error) {
    log.error(`Error getting ${config.resourceName}`, error);
    const payload = {
      id: resource ? resource.id : undefined,
      ...error,
    };
    yield put(config.failureAction(payload, action.meta));
    return payload;
  }

  return response;
}

export function* qcsSyncUpdateResources(config: QcsSyncResourcesConfig, action: any): Generator<*, *, *> {
  const { payload: resources } = action;
  // TODO: add pagination support

  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}${config.resourceBaseUrl}`;
  const requestData = config.transformResourcesToRequestData(resources);
  log.info('Resources', resources, requestData);

  try {
    const response = yield call(qcsAxios.put, requestUrl, requestData, (action.meta && action.meta.axiosConfig) || config.axiosConfig);
    const resultingResources = config.transformResourcesWithResponse(resources, response);
    yield put(config.successAction(resultingResources, { ...action.meta, response }));
    if (action.meta && action.meta.afterSuccessAction) {
      yield put(action.meta.afterSuccessAction);
    }
  } catch (error) {
    log.error(`Error batch ${config.resourceName}`, error);
    if (shouldPostponeAction(action, error)) {
      yield put(addPostponedAction({ action, error, onFailure: () => store.dispatch(config.failureAction(error, action.meta)) }));
    } else {
      yield put(config.failureAction(error, action.meta));
    }
  }
}

// =================================================================================================
// Delete Resource
// =================================================================================================
export function* qcsSyncDeleteResource(config: QcsSyncResourcesConfig, action: any): Generator<*, *, *> {
  const { payload: resource } = action;
  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}${config.resourceBaseUrl}/${resource.id}`;

  let response;
  try {
    response = yield call(qcsAxios.delete, requestUrl, (action.meta && action.meta.axiosConfig) || config.axiosConfig);
    const resultingResource = isImmutable(resource) ? resource.set('isDeleted', true) : ({ ...resource, isDeleted: true });
    yield put(config.successAction(resultingResource, { ...action.meta, response }));
  } catch (error) {
    log.error(`Error deleting ${config.resourceName}`, error);
    const payload = {
      id: resource ? resource.id : undefined,
      ...error,
    };
    if (shouldPostponeAction(action, error)) {
      yield put(addPostponedAction({ action, error, onFailure: () => store.dispatch(config.failureAction(payload, action.meta)) }));
    } else {
      yield put(config.failureAction(payload, action.meta));
    }
    return response;
  }

  return response;
}

export function* qcsSyncPollResource(config: QcsSyncResourcesConfig, action: any): Generator<*, *, *> {
  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}${config.resourceBaseUrl}`;
  let attempt;
  let response;
  for (attempt = 0; attempt < config.pollConfig.attempts; attempt += 1, yield delay(config.pollConfig.pause)) {
    try {
      response = yield call(qcsAxios.get, requestUrl, (action.meta && action.meta.axiosConfig) || config.axiosConfig);
      if (config.pollConfig.isComplete(response, attempt)) {
        break;
      }
    } catch (error) {
      log.error(`Error polling ${config.resourceName}`, error);
      if (config.pollConfig.isComplete(error.response, attempt)) {
        break;
      }
    }
  }
  const resources = config.transformResponseToResources(response);
  if (attempt < config.pollConfig.attempts) {
    yield put(config.successAction(mkQcsSyncResourcesPayload({ resources }), { ...action.meta, response }));
  } else {
    yield put(config.failureAction(mkQcsSyncResourcesPayload({ resources }), { ...action.meta, response }));
  }
}




